import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { scrapeJobsFromUrl, validateUrl, getRateLimitStatus } from '../lib/jobScraper';

export function useJobScraper() {
  const { user } = useAuth();
  const userId = user?.id;

  const [scrapedJobs, setScrapedJobs] = useState([]);
  const [scrapeHistory, setScrapeHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [filters, setFilters] = useState({ department: '', location: '', keyword: '' });
  const [totalCount, setTotalCount] = useState(0);

  // Fetch scraped jobs with filters and pagination
  const fetchScrapedJobs = useCallback(async (filterOverrides, page = 0, limit = 50) => {
    if (!userId) return;
    if (scrapedJobs.length === 0) setLoading(true);

    const f = filterOverrides || filters;

    let query = supabase
      .from('scraped_jobs')
      .select('*, scrape_sessions!inner(careers_url)', { count: 'exact' })
      .eq('hiring_manager_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (f.department) query = query.ilike('department', `%${f.department}%`);
    if (f.location) query = query.ilike('location', `%${f.location}%`);
    if (f.keyword) query = query.or(`title.ilike.%${f.keyword}%,description_snippet.ilike.%${f.keyword}%`);

    const from = page * limit;
    query = query.range(from, from + limit - 1);

    const { data, count, error } = await query;
    if (!error) {
      setScrapedJobs(data || []);
      setTotalCount(count || 0);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, filters]);

  // Fetch scrape session history
  const fetchScrapeHistory = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('scrape_sessions')
      .select('*')
      .eq('hiring_manager_id', userId)
      .order('created_at', { ascending: false });
    if (data) setScrapeHistory(data);
  }, [userId]);

  // Initial fetch
  useEffect(() => {
    fetchScrapedJobs();
    fetchScrapeHistory();
  }, [fetchScrapedJobs, fetchScrapeHistory]);

  // Start a new scrape
  async function startScrape(url) {
    if (!userId) return { error: 'Not authenticated' };

    // Check rate limit before anything else
    const rateStatus = getRateLimitStatus();
    if (!rateStatus.canScrape) {
      return { error: `Please wait ${rateStatus.cooldownSeconds} seconds before scanning again.` };
    }

    // Validate URL
    const validation = validateUrl(url);
    if (!validation.valid) return { error: validation.error.message };

    setScraping(true);

    // Create session via RPC (SECURITY DEFINER — bypasses RLS)
    const { data: sessionId, error: sessionError } = await supabase
      .rpc('create_scrape_session', { p_url: url.trim() });

    if (sessionError || !sessionId) {
      setScraping(false);
      return { error: sessionError?.message || 'Failed to create scrape session' };
    }

    // Update to scraping
    await supabase.rpc('update_scrape_session', {
      p_session_id: sessionId,
      p_status: 'scraping',
    });

    // Run client-side scraper (returns { jobs, error? } — never throws)
    const result = await scrapeJobsFromUrl(url.trim());

    if (result.error) {
      await supabase.rpc('update_scrape_session', {
        p_session_id: sessionId,
        p_status: 'failed',
        p_error: result.error.message,
      });

      setScraping(false);
      await fetchScrapeHistory();
      return { error: result.error.message };
    }

    const parsedJobs = result.jobs;

    if (parsedJobs.length === 0) {
      await supabase.rpc('update_scrape_session', {
        p_session_id: sessionId,
        p_status: 'completed',
        p_jobs_found: 0,
      });

      setScraping(false);
      await fetchScrapeHistory();
      return { sessionId, jobsFound: 0 };
    }

    // Insert scraped jobs via RPC (SECURITY DEFINER — bypasses RLS)
    const { error: insertError } = await supabase.rpc('insert_scraped_jobs', {
      p_jobs: parsedJobs.map(job => ({
        scrape_session_id: sessionId,
        title: job.title,
        department: job.department || null,
        location: job.location || null,
        description_snippet: job.description?.substring(0, 500) || null,
        original_url: job.url || null,
      })),
    });

    if (insertError) {
      await supabase.rpc('update_scrape_session', {
        p_session_id: sessionId,
        p_status: 'failed',
        p_error: insertError.message,
      });

      setScraping(false);
      await fetchScrapeHistory();
      return { error: 'Failed to save results. Please try again.' };
    }

    // Mark completed
    await supabase.rpc('update_scrape_session', {
      p_session_id: sessionId,
      p_status: 'completed',
      p_jobs_found: parsedJobs.length,
    });

    setScraping(false);
    await fetchScrapedJobs();
    await fetchScrapeHistory();
    return { sessionId, jobsFound: parsedJobs.length };
  }

  // Update tags on a scraped job
  async function updateTags(jobId, tags) {
    if (!userId) return { error: 'Not authenticated' };
    const { error } = await supabase
      .from('scraped_jobs')
      .update({ tags, updated_at: new Date().toISOString() })
      .eq('id', jobId)
      .eq('hiring_manager_id', userId);
    if (!error) {
      setScrapedJobs(prev => prev.map(j => j.id === jobId ? { ...j, tags } : j));
    }
    return { error: error?.message };
  }

  // Delete a single scraped job
  async function deleteScrapedJob(jobId) {
    if (!userId) return { error: 'Not authenticated' };
    const { error } = await supabase
      .from('scraped_jobs')
      .delete()
      .eq('id', jobId)
      .eq('hiring_manager_id', userId);
    if (!error) {
      setScrapedJobs(prev => prev.filter(j => j.id !== jobId));
      setTotalCount(prev => prev - 1);
    }
    return { error: error?.message };
  }

  // Delete a scrape session (cascades to its jobs)
  async function deleteSession(sessionId) {
    if (!userId) return { error: 'Not authenticated' };
    const { error } = await supabase
      .from('scrape_sessions')
      .delete()
      .eq('id', sessionId)
      .eq('hiring_manager_id', userId);
    if (!error) {
      await fetchScrapedJobs();
      await fetchScrapeHistory();
    }
    return { error: error?.message };
  }

  // Promote a scraped job to the main jobs table (with optional metadata)
  async function promoteJob(jobId, metadata = {}) {
    if (!userId) return { error: 'Not authenticated' };
    const { data, error } = await supabase
      .rpc('promote_scraped_job', {
        p_scraped_job_id: jobId,
        ...(metadata.role_family && { p_role_family: metadata.role_family }),
        ...(metadata.seniority_band && { p_seniority_band: metadata.seniority_band }),
        ...(metadata.required_skills && { p_required_skills: metadata.required_skills }),
        ...(metadata.preferred_skills && { p_preferred_skills: metadata.preferred_skills }),
        ...(metadata.remote_allowed !== undefined && { p_remote_allowed: metadata.remote_allowed }),
      });
    if (!error && data) {
      // Update local state to reflect promotion
      setScrapedJobs(prev => prev.map(j =>
        j.id === jobId ? { ...j, promoted_job_id: data[0]?.id } : j
      ));
      return { newJob: data[0] };
    }
    return { error: error?.message || 'Failed to promote job' };
  }

  return {
    scrapedJobs,
    scrapeHistory,
    loading,
    scraping,
    filters,
    totalCount,
    startScrape,
    fetchScrapedJobs,
    fetchScrapeHistory,
    updateTags,
    deleteScrapedJob,
    deleteSession,
    promoteJob,
    setFilters,
  };
}
